import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';

/** Ancestors com scroll próprio (ex.: form do modal); scroll de div não dispara em window. */
function getScrollableAncestors(node) {
  const list = [];
  let cur = node?.parentElement;
  while (cur && cur !== document.body) {
    const st = window.getComputedStyle(cur);
    const oy = st.overflowY;
    const ox = st.overflowX;
    const scrollY = /(auto|scroll|overlay)/.test(oy) && cur.scrollHeight > cur.clientHeight + 1;
    const scrollX = /(auto|scroll|overlay)/.test(ox) && cur.scrollWidth > cur.clientWidth + 1;
    if (scrollY || scrollX) list.push(cur);
    cur = cur.parentElement;
  }
  return list;
}

/**
 * Select estilo menu (lista em portal).
 * `portalRootRef`: painel com `position: relative` (ex.: folha do modal); o menu usa
 * `absolute` + z-index baixo para ficar abaixo do cabeçalho. Sem isso, cai em `document.body` + fixed.
 * `ariaLabelledBy`: id do texto de legenda (sem `<label htmlFor>` para não focar ao clicar no título).
 */
const MenuDropdown = ({
  id,
  name,
  value,
  onChange,
  options,
  placeholder = 'Selecione…',
  required = false,
  disabled = false,
  className = '',
  portalRootRef = null,
  ariaLabelledBy
}) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const [menuStyle, setMenuStyle] = useState({
    position: 'fixed',
    top: 0,
    left: 0,
    width: 0,
    maxHeight: 240,
    zIndex: 100020
  });

  const selectedLabel = options.find((o) => o.value === value)?.label ?? '';

  const syncPosition = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const tr = el.getBoundingClientRect();
    const root = portalRootRef?.current;
    if (root) {
      const sr = root.getBoundingClientRect();
      const top = tr.bottom - sr.top + 4;
      const left = tr.left - sr.left;
      const width = tr.width;
      const spaceBelow = sr.bottom - tr.bottom - 12;
      const maxHeight = Math.max(72, Math.min(240, spaceBelow));
      setMenuStyle({
        position: 'absolute',
        top,
        left,
        width,
        maxHeight,
        zIndex: 40
      });
    } else {
      const top = tr.bottom + 4;
      const maxHeight = Math.max(72, Math.min(240, window.innerHeight - top - 12));
      setMenuStyle({
        position: 'fixed',
        top,
        left: tr.left,
        width: tr.width,
        maxHeight,
        zIndex: 100020
      });
    }
  }, [portalRootRef]);

  useLayoutEffect(() => {
    if (!open) return;
    syncPosition();
    // Fecha ao rolar página/ancestros (menu alinhado ao trigger), mas não quando o scroll é só
    // da lista do menu — senão o wheel propaga para o form do modal e dispara scroll no pai.
    const closeOnScroll = (e) => {
      const t = e?.target;
      if (t instanceof Node && menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onResize = () => syncPosition();
    const scrollParents = getScrollableAncestors(triggerRef.current);
    scrollParents.forEach((p) => p.addEventListener('scroll', closeOnScroll, { passive: true }));
    window.addEventListener('scroll', closeOnScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      scrollParents.forEach((p) => p.removeEventListener('scroll', closeOnScroll));
      window.removeEventListener('scroll', closeOnScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open, syncPosition]);

  useEffect(() => {
    if (!open) return;
    const onDocDown = (e) => {
      if (triggerRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDocDown);
    return () => document.removeEventListener('mousedown', onDocDown);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const pick = (nextValue) => {
    onChange({ target: { name, value: nextValue } });
    setOpen(false);
  };

  const triggerClass =
    'flex w-full items-center justify-between gap-2 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-left text-sm transition-shadow disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-0 ' +
    (open ? 'ring-2 ring-primary-500 ' : '') +
    className;

  return (
    <div className="relative w-full">
      <select
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        disabled={disabled}
        tabIndex={-1}
        aria-hidden="true"
        className="sr-only"
      >
        {options.map((opt) => (
          <option key={opt.value === '' ? `${name}-empty` : String(opt.value)} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>

      <button
        ref={triggerRef}
        type="button"
        id={id}
        disabled={disabled}
        className={triggerClass}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-labelledby={ariaLabelledBy || undefined}
        onClick={() => !disabled && setOpen((o) => !o)}
      >
        <span className={selectedLabel ? 'truncate text-gray-900' : 'truncate text-gray-400'}>
          {selectedLabel || placeholder}
        </span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`}
          aria-hidden
        />
      </button>

      {open &&
        createPortal(
          <div
            ref={menuRef}
            role="listbox"
            className="overflow-y-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
            style={{
              position: menuStyle.position,
              top: menuStyle.top,
              left: menuStyle.left,
              width: menuStyle.width,
              maxHeight: menuStyle.maxHeight,
              zIndex: menuStyle.zIndex
            }}
            onWheel={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
          >
            {options.map((opt) => (
              <button
                key={opt.value === '' ? `${name}-empty-btn` : String(opt.value)}
                type="button"
                role="option"
                aria-selected={value === opt.value}
                className={`w-full px-3 py-2 text-left text-sm text-gray-900 hover:bg-gray-100 ${
                  value === opt.value ? 'bg-primary-50 font-medium text-primary-600' : ''
                }`}
                onClick={() => pick(opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>,
          portalRootRef?.current ?? document.body
        )}
    </div>
  );
};

export default MenuDropdown;
